import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Button, Row, Col, Card } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useGetNewsById } from '../../api/hooks/useNewsQuery';
import { Image } from 'antd';

const NewsDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const newsId = id ? parseInt(id, 10) : 0;

  const { data: news, isLoading, error } = useGetNewsById(newsId);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-red-600">{t('newsNotFound')}</h1>
          <Button onClick={() => navigate('/')} icon={<ArrowLeftOutlined />}>
            {t('backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Button onClick={() => navigate('/')} icon={<ArrowLeftOutlined />} className="mb-4">
          {t('backToHome')}
        </Button>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={16}>
          <Card className="shadow-lg">
            <div className="news-detail">
              <h1 className="mb-4 text-3xl font-bold text-gray-800">{news.title}</h1>

              <div className="mb-6">
                <p className="text-lg leading-relaxed text-gray-600">{news.summary}</p>
              </div>

              {news.preview && (
                <div className="mb-6">
                  <Image
                    src={news.preview}
                    alt={news.title}
                    className="w-full rounded-lg"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
              )}

              <div className="prose max-w-none">
                <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                  {news.description}
                </p>
              </div>

              <div className="mt-8 border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>
                    {t('publishedOn')}: {new Date(news.createTime).toLocaleDateString()}
                  </span>
                  {news.updateTime !== news.createTime && (
                    <span>
                      {t('updatedOn')}: {new Date(news.updateTime).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title={t('relatedNews')} className="shadow-lg">
            <p className="text-gray-500">{t('moreNewsComingSoon')}</p>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NewsDetail;
